import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin } from "lucide-react";
import { API, imgUrl } from "../lib/api";
import { useUserLocation } from "../lib/location";

// Auto-scrolling, swipeable strip of states; tapping one sets the global location to that state.
export default function StatesSlider({ onPick }) {
  const { location, setLocation } = useUserLocation();
  const [data, setData] = useState(null);
  const ref = useRef(null);
  const pause = useRef(0);

  useEffect(() => { axios.get(`${API}/state-banners`).then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  const items = data?.enabled ? data.items : [];
  const speed = data?.speed || 4;

  useEffect(() => {
    const el = ref.current;
    if (!el || !items.length) return;
    let raf;
    const step = () => {
      if (Date.now() > pause.current) {
        el.scrollLeft += speed * 0.25;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [items.length, speed]);

  if (!items.length) return null;
  const hold = () => { pause.current = Date.now() + 3000; };
  const pick = (s) => { setLocation({ state: s, city: "" }); onPick?.(s); };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-testid="states-slider">
      <div className="flex items-end justify-between mb-3">
        <div><p className="eyebrow !text-[10px] mb-1">Explore by state</p><h2 className="font-display text-2xl sm:text-3xl">All States & UTs</h2></div>
        <p className="text-[11px] text-gray-500 hidden sm:block">Swipe & tap a state</p>
      </div>
      <div ref={ref} className="flex gap-3 overflow-x-auto no-scrollbar pb-1 select-none" onPointerDown={hold} onTouchStart={hold} onWheel={hold} onMouseEnter={() => { pause.current = Infinity; }} onMouseLeave={hold} data-testid="states-track">
        {[...items, ...items].map((b, k) => {
          const on = location?.state === b.state && !location?.city;
          return (
            <button key={`${b.id}-${k}`} onClick={() => pick(b.state)} aria-hidden={k >= items.length} tabIndex={k >= items.length ? -1 : 0}
              className={`keep-dark group relative shrink-0 w-36 sm:w-44 aspect-[4/3] rounded-xl overflow-hidden border transition-colors ${on ? "border-[#D4AF37]" : "border-white/10 hover:border-[#D4AF37]/60"}`}
              data-testid={k < items.length ? `state-card-${b.state.replace(/\s+/g, "-").toLowerCase()}` : undefined}>
              <img src={imgUrl(b.image_url)} alt="" loading="lazy" draggable={false} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <p className="absolute bottom-2 left-2.5 right-2.5 text-left text-sm font-semibold leading-tight flex items-center gap-1"><MapPin className="w-3 h-3 text-[#D4AF37] shrink-0" />{b.state}</p>
            </button>
          );
        })}
      </div>
    </section>
  );
}
